import React from 'react';
import { StatCard } from '../../../components/shared/StatCard';
import type { TimeEntry } from '../../../core/models/TimeEntry';
import { Clock, DollarSign, XCircle, AlertCircle } from 'lucide-react';

interface TimeEntrySummaryProps {
  entries: TimeEntry[];
  dateRange?: {
    start: Date;
    end: Date;
  };
}

export const TimeEntrySummary: React.FC<TimeEntrySummaryProps> = ({
  entries,
  dateRange,
}) => {
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}m`;
    }
  };

  const totalDuration = entries.reduce((sum, entry) => sum + entry.duration, 0);
  const billableDuration = entries
    .filter((entry) => entry.billable)
    .reduce((sum, entry) => sum + entry.duration, 0);
  const rejectedCount = entries.filter(
    (entry) => entry.status === 'rejected'
  ).length;
  const pendingCount = entries.filter(
    (entry) => entry.status === 'pending'
  ).length;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        title="Total Time"
        value={formatDuration(totalDuration)}
        icon={Clock}
        description={dateRange ? 'Selected period' : 'All time'}
      />
      <StatCard
        title="Billable Time"
        value={formatDuration(billableDuration)}
        icon={DollarSign}
        description={`${Math.round((billableDuration / totalDuration) * 100) || 0}% of total`}
      />
      <StatCard
        title="Rejected"
        value={rejectedCount.toString()}
        icon={XCircle}
        description={`${entries.length > 0 ? Math.round((rejectedCount / entries.length) * 100) : 0}% of entries`}
      />
      <StatCard
        title="Pending"
        value={pendingCount.toString()}
        icon={AlertCircle}
        description="Not billed yet"
      />
    </div>
  );
};
